import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ShieldCheck,
  ArrowLeft,
  Check,
  X,
  Lock,
  Coins,
  KeyRound,
  RefreshCw,
  Loader2,
  Server,
  BellRing,
  AlertTriangle,
  Play as PlayIcon,
  ReceiptText,
  Fingerprint,
} from "lucide-react";
import { useAuth } from "../App";
import DualCosignManager from "../components/DualCosignManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Payouts approval console for the platform admin.
//
// Security posture:
//   • Route is inside <AdminOnlyRoute> so only PLATFORM_OWNER_EMAIL can render.
//   • Approve button is HIDDEN by default. Reveal it by:
//       (a) typing "reveal" with the mouse focused anywhere on the page, OR
//       (b) triple-clicking the shield header, OR
//       (c) holding Ctrl + Shift + A for 800 ms.
//     The reveal times out after 30 seconds and hides all Approve buttons again.
//   • Even after reveal, the SERVER requires an `X-Payout-Approval-Pin` header
//     that the admin types into the prompt — the PIN is never stored in
//     localStorage, only kept in memory for the current tab.
//
// Combined with the JWT admin session and a per-approval PIN, this makes an
// accidental click or a leaked session token insufficient to release funds.
const AdminPayouts = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [revealed, setRevealed] = useState(false);
  const [adapter, setAdapter] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const revealTimerRef = useRef(null);
  const keySeqRef = useRef({ buf: "", ts: 0 });
  const headerClickRef = useRef({ count: 0, ts: 0 });

  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/payouts${tab === "all" ? "" : `?status=${tab}`}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load payout requests");
    } finally {
      setLoading(false);
    }
  }, [token, tab]);

  const fetchAdapter = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payments/adapter/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdapter(data);
    } catch { /* non-fatal */ }
  }, [token]);

  const fetchLedger = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ledger/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLedger(data);
    } catch { /* non-fatal */ }
  }, [token]);

  const fetchDigest = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/treasury/digest/latest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDigest(data?.digest || null);
    } catch { /* non-fatal */ }
  }, [token]);

  const runDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/treasury/digest/run`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDigest(data?.digest || null);
      toast.success(
        data?.digest?.all_clear
          ? `All clear — ${data.digest.total_platform_balance_display} on platform`
          : `Anomalies flagged: ${data?.digest?.anomalies?.count || 0}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest failed");
    } finally {
      setDigestBusy(false);
    }
  };

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchAdapter(); fetchLedger(); fetchDigest(); }, [fetchAdapter, fetchLedger, fetchDigest]);

  // --- Hidden-button reveal wiring --- //
  const doReveal = useCallback(() => {
    if (revealed) return;
    setRevealed(true);
    toast.success("Approval mode ON — auto-hides in 30 s", { duration: 4000 });
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    revealTimerRef.current = setTimeout(() => {
      setRevealed(false);
      toast("Approval mode auto-locked");
    }, 30_000);
  }, [revealed]);

  useEffect(() => {
    const onKey = (e) => {
      // Ctrl + Shift + A
      if (e.ctrlKey && e.shiftKey && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        doReveal();
        return;
      }
      // typed "reveal" (case-insensitive, 2-second window)
      const now = Date.now();
      const ch = (e.key || "").toLowerCase();
      if (ch.length !== 1) return;
      if (now - keySeqRef.current.ts > 2000) keySeqRef.current.buf = "";
      keySeqRef.current.buf = (keySeqRef.current.buf + ch).slice(-8);
      keySeqRef.current.ts = now;
      if (keySeqRef.current.buf.endsWith("reveal")) doReveal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doReveal]);

  const onHeaderClick = () => {
    // Triple-click the shield header within 1s to reveal.
    const now = Date.now();
    if (now - headerClickRef.current.ts > 1000) headerClickRef.current.count = 0;
    headerClickRef.current.count += 1;
    headerClickRef.current.ts = now;
    if (headerClickRef.current.count >= 3) {
      headerClickRef.current.count = 0;
      doReveal();
    }
  };

  const approve = async (payout) => {
    // Two-person cosign: first click records the first signature. Sole admin
    // or emergency: also accept a MASTER_SECURITY_PIN to bypass cosign.
    const isCosignReady = payout.status === "cosign_pending" && payout.first_approver_email;
    const stagePrompt = isCosignReady
      ? `A second admin's signature is required to release $${(payout.amount_cents / 100).toFixed(2)} to ${payout.artist_name}.\n\nFirst approver was ${payout.first_approver_email}.\n\nEnter PAYOUT_APPROVAL_PIN to cosign & finalize:`
      : `Enter PAYOUT_APPROVAL_PIN to record your signature.\n\nThis is the FIRST of two signatures required to release $${(payout.amount_cents / 100).toFixed(2)} to ${payout.artist_name}. A different admin must cosign afterwards.\n\n(Advanced: append " MASTER=<master-security-pin>" to bypass cosign in an emergency.)`;
    const raw = window.prompt(stagePrompt, "");
    if (!raw) return;
    // Parse: "<pin> MASTER=<masterPin>"
    let pin = raw.trim();
    let masterPin = "";
    const masterIdx = pin.indexOf("MASTER=");
    if (masterIdx !== -1) {
      masterPin = pin.slice(masterIdx + "MASTER=".length).trim();
      pin = pin.slice(0, masterIdx).trim();
    }
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Payout-Approval-Pin": pin,
      };
      if (masterPin) headers["X-Master-Security-Pin"] = masterPin;
      const { data } = await axios.post(
        `${API}/admin/payouts/${payout.payout_id}/approve`,
        {},
        { headers }
      );
      if (data.status === "cosign_pending") {
        toast(
          `First signature recorded (${data.first_approver}). A different admin must cosign to release funds.`,
          { duration: 6000 }
        );
      } else if (data.master_override_used) {
        toast.success(`Approved via MASTER override — $${(payout.amount_cents / 100).toFixed(2)} released`);
      } else {
        toast.success(`Cosigned & approved — $${(payout.amount_cents / 100).toFixed(2)} released`);
      }
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    }
  };

  const reject = async (payout) => {
    const memo = window.prompt(`Reason for rejecting payout to ${payout.artist_name}?`, "");
    if (memo === null) return;
    try {
      await axios.post(
        `${API}/admin/payouts/${payout.payout_id}/reject?memo=${encodeURIComponent(memo)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rejected");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    }
  };

  // Linda + Kwai Yuk Mak dual-cosign approval — the "Linda is the boss.
  // Kwai Yuk Mak is the boss." button asks for BOTH name-scoped passwords
  // and the shared PAYOUT_APPROVAL_PIN, then finalizes the release.
  const dualCosignApprove = async (payout) => {
    const linda = window.prompt(
      `Linda's password (dual-cosign #1)\n\nReleasing $${(payout.amount_cents / 100).toFixed(2)} to ${payout.artist_name}.\nFirst signer: ${payout.first_approver_email || "—"}`,
      "",
    );
    if (!linda) return;
    const kwai = window.prompt("Kwai Yuk Mak's password (dual-cosign #2)", "");
    if (!kwai) return;
    const pin = window.prompt("PAYOUT_APPROVAL_PIN", "");
    if (!pin) return;
    try {
      await axios.post(
        `${API}/admin/payouts/${payout.payout_id}/dual-cosign-approve`,
        { linda_password: linda, kwai_password: kwai },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Payout-Approval-Pin": pin,
          },
        },
      );
      toast.success(`Dual-cosign approved — $${(payout.amount_cents / 100).toFixed(2)} released`);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dual-cosign failed");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white pb-24">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center"
            data-testid="payouts-back-btn"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">StardomFans · Admin</span>
          </Link>
          <button
            onClick={onHeaderClick}
            className="ml-auto flex items-center gap-2 text-right group select-none"
            data-testid="reveal-header"
            title="Confidential — Payouts"
          >
            <ShieldCheck className={`w-5 h-5 transition-colors ${revealed ? "text-[#EC008C]" : "text-[#8A92A0]"}`} />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#8A92A0] group-hover:text-white">Treasury</p>
              <p className="text-white font-bold">Payout Approvals</p>
            </div>
          </button>
          <Link
            to="/admin/refunds"
            className="hidden sm:inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#1A2030] text-[#8A92A0] hover:text-white hover:border-[#EC008C]/40 text-xs"
            data-testid="link-to-refunds"
          >
            <ReceiptText className="w-3.5 h-3.5" />
            Refunds
          </Link>
          <Link
            to="/admin/payouts/external"
            className="hidden sm:inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#EC008C]/40 text-[#EC008C] hover:text-white hover:bg-[#EC008C]/10 text-xs"
            data-testid="link-to-external-payouts"
          >
            <Coins className="w-3.5 h-3.5" />
            External Ledger
          </Link>
          <Link
            to="/admin/verifications"
            className="hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#1A2030] text-[#8A92A0] hover:text-white hover:border-[#EC008C]/40 text-xs"
            data-testid="link-to-verifications"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            KYC
          </Link>
          <Link
            to="/admin/cra"
            className="hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#1A2030] text-[#8A92A0] hover:text-white hover:border-[#EC008C]/40 text-xs"
            data-testid="link-to-cra"
          >
            <ReceiptText className="w-3.5 h-3.5" />
            CRA
          </Link>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12 space-y-8">
        {/* Confidential banner */}
        <div className={`rounded-xl border p-4 flex items-start gap-3 transition-colors ${revealed ? "border-[#EC008C]/40 bg-[#EC008C]/[0.07]" : "border-[#1A2030] bg-[#0A0E18]"}`}>
          <Lock className={`w-4 h-4 mt-0.5 ${revealed ? "text-[#EC008C]" : "text-[#8A92A0]"}`} />
          <div className="text-xs text-[#8A92A0] flex-1">
            {revealed ? (
              <>
                <strong className="text-[#EC008C]">Approval mode is ON.</strong> Approve buttons are visible for the next 30 seconds. The server still demands the treasury PIN before releasing any funds.
              </>
            ) : (
              <>
                Approve buttons are hidden. Reveal by <kbd className="px-1.5 py-0.5 rounded bg-[#1A2030] text-white text-[10px]">Ctrl+Shift+A</kbd>, typing <code className="px-1 rounded bg-[#1A2030] text-white text-[10px]">reveal</code>, or triple-clicking the shield above.
              </>
            )}
          </div>
        </div>

        {/* Dual-cosign password manager — only Linda and the platform owner see this */}
        <DualCosignManager token={token} userEmail={user?.email} />

        {/* Daily Treasury Digest — signed ledger + anomaly one-glance summary */}
        <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-4 sm:p-5" data-testid="treasury-digest-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                digest?.all_clear ? "bg-[#8DB87A]/15 border border-[#8DB87A]/30" : "bg-[#EC008C]/15 border border-[#EC008C]/30"
              }`}>
                <BellRing className={`w-4 h-4 ${digest?.all_clear ? "text-[#8DB87A]" : "text-[#EC008C]"}`} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Daily Treasury Digest</p>
                <h2 className="text-white font-bold">
                  {digest
                    ? digest.all_clear
                      ? "All clear — ledger verified, no anomalies"
                      : `Anomalies flagged (${digest.anomalies?.count || 0})`
                    : "No digest yet — run one now"}
                </h2>
                {digest?.generated_at && (
                  <p className="text-[11px] text-[#8A92A0] mt-0.5">
                    Last scan: {digest.generated_at.slice(0, 19).replace("T", " ")} UTC
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={runDigest}
              disabled={digestBusy}
              className="h-9 px-4 rounded-full bg-[#EC008C] text-black text-xs font-bold hover:bg-[#FF3399] disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="digest-run-btn"
            >
              <PlayIcon className="w-3.5 h-3.5" />
              {digestBusy ? "Scanning…" : "Run digest now"}
            </button>
          </div>

          {digest && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="digest-grid">
              <StatTile
                label="Ledger integrity"
                value={digest.ledger_integrity?.valid ? "OK" : "FAIL"}
                sublabel={`${digest.ledger_integrity?.users_scanned || 0} users · ${digest.ledger_integrity?.total_rows || 0} rows`}
                good={digest.ledger_integrity?.valid}
                testid="digest-integrity"
              />
              <StatTile
                label="Platform balance"
                value={digest.total_platform_balance_display}
                sublabel="Sum of all wallets"
                good
                testid="digest-balance"
              />
              <StatTile
                label="Pending payouts"
                value={String(digest.payouts?.pending_count || 0)}
                sublabel={`${digest.payouts?.outstanding_display} outstanding`}
                good={(digest.payouts?.pending_count || 0) === 0}
                testid="digest-payouts"
              />
              <StatTile
                label="Anomalies"
                value={String(digest.anomalies?.count || 0)}
                sublabel="Last 24 h"
                good={(digest.anomalies?.count || 0) === 0}
                testid="digest-anomalies"
              />
            </div>
          )}

          {digest && !digest.all_clear && (
            <div className="mt-4 space-y-2" data-testid="digest-anomaly-list">
              {(digest.anomalies?.credit_spikes_24h || []).slice(0, 3).map((s) => (
                <div key={s.user_id} className="flex items-center gap-2 text-xs text-[#EBB84C]">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  Credit spike: <span className="font-mono text-white">{s.email || s.user_id}</span> +${(s.credited_cents / 100).toFixed(2)} in 24h
                </div>
              ))}
              {(digest.anomalies?.oversized_payouts || []).slice(0, 3).map((p) => (
                <div key={p.payout_id} className="flex items-center gap-2 text-xs text-[#EBB84C]">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  Oversized payout: {p.artist_name} — ${(p.amount_cents / 100).toFixed(2)} ({p.reason})
                </div>
              ))}
              {(digest.anomalies?.master_override_uses_24h || []).slice(0, 3).map((m) => (
                <div key={m.payout_id} className="flex items-center gap-2 text-xs text-[#EC008C]">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  MASTER override: {m.payout_id} — ${((m.amount_cents || 0) / 100).toFixed(2)} by {m.decided_by}
                </div>
              ))}
              {(digest.anomalies?.suspicious_sessions?.failed_login_bursts || []).slice(0, 3).map((f) => (
                <div key={f.email} className="flex items-center gap-2 text-xs text-[#EBB84C]">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  Failed-login burst: <span className="font-mono text-white">{f.email}</span> · {f.attempts_24h} attempts from {f.distinct_ips} IPs
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Adapter + ledger health strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-4" data-testid="adapter-status-card">
            <div className="flex items-center gap-2 mb-3">
              <Server className="w-4 h-4 text-[#EC008C]" />
              <h2 className="text-sm font-bold uppercase tracking-widest text-[#8A92A0]">Payment Adapter</h2>
            </div>
            {adapter ? (
              <>
                <p className="text-xs text-[#8A92A0]">Active: <span className="text-white font-mono">{adapter.active}</span></p>
                <ul className="mt-2 space-y-1">
                  {(adapter.providers || []).map((p) => (
                    <li key={p.provider} className="flex items-center justify-between text-xs">
                      <span className="font-mono text-white">{p.provider}</span>
                      <span className={`px-2 py-0.5 rounded font-semibold ${p.configured ? "bg-[#8DB87A]/15 text-[#8DB87A]" : "bg-[#4A5260]/20 text-[#4A5260]"}`}>
                        {p.configured ? "configured" : "not configured"}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-xs text-[#4A5260]">Loading…</p>
            )}
          </div>

          <div className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-4" data-testid="ledger-status-card">
            <div className="flex items-center gap-2 mb-3">
              <Coins className="w-4 h-4 text-[#EC008C]" />
              <h2 className="text-sm font-bold uppercase tracking-widest text-[#8A92A0]">Signed Token Ledger</h2>
            </div>
            {ledger ? (
              <>
                <p className="text-xs">
                  Chain integrity:{" "}
                  <span className={`px-2 py-0.5 rounded font-bold ${ledger.valid ? "bg-[#8DB87A]/15 text-[#8DB87A]" : "bg-[#EC008C]/15 text-[#EC008C]"}`}>
                    {ledger.valid ? "VALID" : "TAMPER DETECTED"}
                  </span>
                </p>
                <p className="text-[11px] text-[#8A92A0] mt-2">
                  {ledger.users_scanned} users · {ledger.total_rows} signed rows
                </p>
                {!ledger.valid && ledger.invalid?.length > 0 && (
                  <ul className="mt-2 text-[11px] text-[#EC008C]">
                    {ledger.invalid.map((b) => (
                      <li key={b.at_entry}>Broke at {b.at_entry}: {b.reason}</li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <p className="text-xs text-[#4A5260]">Loading…</p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2">
          {["pending", "cosign_pending", "approved", "rejected", "all"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${tab === t ? "bg-[#EC008C] text-black" : "bg-[#0A0E18] border border-[#1A2030] text-[#8A92A0] hover:bg-[#1A2030]"}`}
              data-testid={`payouts-tab-${t}`}
            >
              {t === "cosign_pending" ? "Cosign" : t}
            </button>
          ))}
          <button
            onClick={fetchRows}
            className="ml-auto h-9 px-3 rounded-full border border-[#1A2030] hover:bg-[#1A2030] text-xs flex items-center gap-1.5 text-[#8A92A0]"
            data-testid="payouts-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {/* Table */}
        <div className="rounded-xl overflow-hidden border border-[#1A2030] bg-[#0A0E18]" data-testid="payouts-table">
          {loading ? (
            <div className="p-8 flex items-center gap-3 text-[#8A92A0]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading payout queue…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-[#4A5260]">No {tab} payout requests.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#050810] text-[#8A92A0] text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left p-3">Artist</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">Requested</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Note</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.payout_id} className="border-t border-[#1A2030] hover:bg-[#050810]/60" data-testid={`payout-row-${r.payout_id}`}>
                    <td className="p-3">
                      <p className="font-bold text-white">{r.artist_name || r.artist_stage_name || "—"}</p>
                      <p className="text-[11px] font-mono text-[#4A5260]">{r.artist_id}</p>
                      {r.payout_channels && (r.payout_channels.interac_email || r.payout_channels.paypal_handle || r.payout_channels.stripe_link) && (
                        <div className="flex flex-col gap-1 mt-2" data-testid={`payout-channels-${r.payout_id}`}>
                          {r.payout_channels.interac_email && (
                            <a
                              href={`mailto:${r.payout_channels.interac_email}`}
                              className="inline-flex items-center gap-1.5 text-[10px] text-[#EC008C] hover:underline"
                              data-testid={`pay-interac-${r.payout_id}`}
                            >
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#EC008C]" />
                              Interac · {r.payout_channels.interac_email}
                            </a>
                          )}
                          {r.payout_channels.paypal_handle && (
                            <span
                              className="inline-flex items-center gap-1.5 text-[10px] text-[#c0c9d4] cursor-pointer"
                              onClick={() => {
                                navigator.clipboard?.writeText(r.payout_channels.paypal_handle);
                                toast.success("PayPal handle copied");
                              }}
                              data-testid={`pay-paypal-${r.payout_id}`}
                              title="Click to copy"
                            >
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0070BA]" />
                              PayPal · {r.payout_channels.paypal_handle}
                            </span>
                          )}
                          {r.payout_channels.stripe_link && (
                            <a
                              href={r.payout_channels.stripe_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-[10px] text-[#635BFF] hover:underline truncate max-w-[220px]"
                              data-testid={`pay-stripe-${r.payout_id}`}
                            >
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#635BFF]" />
                              Stripe · Open link
                            </a>
                          )}
                          {r.payout_channels.notes && (
                            <p className="text-[10px] text-[#8A92A0] italic">
                              {r.payout_channels.notes}
                            </p>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-bold text-white">
                      ${(r.amount_cents / 100).toFixed(2)} <span className="text-[10px] text-[#8A92A0] uppercase">{r.currency}</span>
                    </td>
                    <td className="p-3 text-xs text-[#8A92A0]">{r.requested_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${
                        r.status === "pending"
                          ? "bg-[#EBB84C]/15 text-[#EBB84C]"
                          : r.status === "cosign_pending"
                          ? "bg-[#B87AB8]/15 text-[#B87AB8]"
                          : r.status === "approved"
                          ? "bg-[#8DB87A]/15 text-[#8DB87A]"
                          : "bg-[#4A5260]/20 text-[#8A92A0]"
                      }`}>
                        {r.status === "cosign_pending" ? "COSIGN NEEDED" : r.status}
                      </span>
                      {r.status === "cosign_pending" && r.first_approver_email && (
                        <p className="text-[10px] text-[#8A92A0] mt-1">
                          1st: <span className="font-mono">{r.first_approver_email}</span>
                        </p>
                      )}
                      {r.master_override_used && (
                        <p className="text-[10px] text-[#EC008C] mt-1 uppercase tracking-widest">Master override</p>
                      )}
                    </td>
                    <td className="p-3 text-xs text-[#8A92A0] max-w-xs truncate" title={r.note}>{r.note || "—"}</td>
                    <td className="p-3 text-right">
                      {(r.status === "pending" || r.status === "cosign_pending") ? (
                        <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                          <button
                            onClick={() => reject(r)}
                            className="h-8 px-3 rounded-full border border-[#4A5260] text-[#8A92A0] hover:bg-[#1A2030] text-xs flex items-center gap-1"
                            data-testid={`reject-btn-${r.payout_id}`}
                          >
                            <X className="w-3 h-3" /> Reject
                          </button>
                          <button
                            onClick={() => approve(r)}
                            className={`h-8 px-3 rounded-full bg-[#EC008C] text-black text-xs font-bold flex items-center gap-1 hover:bg-[#FF3399] transition-all ${revealed ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none w-0 !p-0 !border-0"}`}
                            data-testid={`approve-btn-${r.payout_id}`}
                            aria-hidden={!revealed}
                            tabIndex={revealed ? 0 : -1}
                          >
                            <KeyRound className="w-3 h-3" /> Approve
                          </button>
                          {/* LINDA-ONLY co-signing final release button.
                            * Visibility rules (all must be true):
                            *   1. Row is at `cosign_pending` — first admin has already signed
                            *   2. The first approver is NOT Linda (she can't self-cosign)
                            *   3. The currently-logged-in admin's email is exactly linda8mak@gmail.com
                            *   4. Reveal mode is ON (same 30-sec timeout as the normal Approve button)
                            * The button just calls the same /payouts/approve endpoint — the
                            * server-side two-person cosign gate still enforces the actual rule. */}
                          {r.status === "cosign_pending" &&
                           (user?.email || "").toLowerCase() === "linda8mak@gmail.com" &&
                           (r.first_approver_email || "").toLowerCase() !== "linda8mak@gmail.com" &&
                           revealed && (
                            <button
                              onClick={() => dualCosignApprove(r)}
                              className="h-8 px-4 rounded-full bg-gradient-to-r from-[#EC008C] via-[#FF3399] to-[#F0C464] text-black text-xs font-bold flex items-center gap-1.5 hover:brightness-110 shadow-lg shadow-[#EC008C]/30"
                              data-testid={`linda-cosign-btn-${r.payout_id}`}
                              title="Requires Linda's password + Kwai Yuk Mak's password + PAYOUT_APPROVAL_PIN"
                            >
                              <Fingerprint className="w-3.5 h-3.5" />
                              Linda is the boss. Kwai Yuk Mak is the boss.
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#4A5260]">
                          {r.decided_at ? r.decided_at.slice(0, 16).replace("T", " ") : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPayouts;

const StatTile = ({ label, value, sublabel, good, testid }) => (
  <div
    className={`rounded-lg border p-3 ${good ? "border-[#1A2030] bg-[#050810]" : "border-[#EC008C]/30 bg-[#EC008C]/[0.06]"}`}
    data-testid={testid}
  >
    <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">{label}</p>
    <p className={`text-xl font-black ${good ? "text-white" : "text-[#EC008C]"}`}>{value}</p>
    {sublabel && <p className="text-[10px] text-[#4A5260] mt-0.5">{sublabel}</p>}
  </div>
);
